import format from 'date-fns/format'
import addYears from 'date-fns/addYears'
import addMonths from 'date-fns/addMonths'
import subMonths from 'date-fns/subMonths'
import addWeeks from 'date-fns/addWeeks'
import addDays from 'date-fns/addDays'
import toDate from 'date-fns/toDate'
import addMinutes from 'date-fns/addMinutes'
import differenceInDays from 'date-fns/differenceInDays'
import currency from 'currency.js'

export const asCurrency = (input) => {
  return currency(input).format()
}

export const asPercentage = (input) => {
  const asNum = Number(input)
  return `${asNum.toFixed(2)}%`
}

export const normalizedDate = (input) => {
  const offset = new Date().getTimezoneOffset()
  return toDate(addMinutes(new Date(input), offset))
}

export const asDate = (input) => {
  const date = normalizedDate(input)
  return format(date, 'M/d/yyyy')
}

export const validateFields = (_fields) => {
  const fields = [..._fields]
  let isValid = true

  fields.forEach((field) => {
    if (field.value === '') {
      field.hasError = true
      isValid = false
    }
  })

  return {
    fields,
    isValid,
  }
}

export const updateValuesFromFields = (_values, _fields) => {
  const values = { ..._values }
  const fields = [..._fields]

  fields.forEach((field) => {
    let value = field.value

    if (field.dataType === 'int') {
      value = parseInt(value)
    }

    if (field.dataType === 'float') {
      value = parseFloat(value)
    }

    values[field.name] = value
  })

  return values
}

export const getPrincipal = (monthlyPayment, interestRate, numPayments) => {
  const monthlyRate = interestRate / 100 / 12
  const principal = currency(monthlyPayment / monthlyRate).multiply(1 - Math.pow(1 + monthlyRate, -numPayments))

  return principal
}

export const getMonthlyPayment = (principal, interestRate, numPayments) => {
  const monthlyRate = interestRate / 100 / 12

  return currency(principal).multiply(monthlyRate / (1 - Math.pow(1 + monthlyRate, -numPayments)))
}

export const getInterestRate = (principal, monthlyPayment, numPayments) => {
  let minRate = 0,
    midRate = 0,
    maxRate = 500

  while (minRate < maxRate - 0.00001) {
    midRate = (minRate + maxRate) / 2
    let j = midRate / 1200
    let guessed_pmt = principal * (j / (1 - Math.pow(1 + j, numPayments * -1)))

    if (guessed_pmt > monthlyPayment) {
      maxRate = midRate
    } else {
      minRate = midRate
    }
  }

  return midRate
}

export const getNumPayments = (balance, monthlyPayment, interestRate) => {
  const monthlyRate = interestRate / 100 / 12
  const numPayments = Math.ceil(
    (-1 * Math.log(1 - (monthlyRate * balance) / monthlyPayment)) / Math.log(1 + monthlyRate)
  )

  if (isNaN(numPayments)) {
    return 1
  }

  return numPayments
}

export const getInflationIncrease = (currentValue, numYears, inflationRate) => {
  let value = currentValue
  const rate = 1 + inflationRate / 100
  const schedule = []

  for (let count = 0; count < numYears; count++) {
    let eventNum = count + 1
    const newValue = rate * value
    const diffValue = newValue - value
    value = newValue

    schedule.push({
      eventNum,
      diffValue,
      value,
    })
  }

  return {
    currentValue,
    newValue: value,
    numYears,
    schedule,
  }
}

export const getInflationDecrease = (currentValue, numYears, inflationRate) => {
  let value = currentValue
  const rate = 1 + inflationRate / 100
  const schedule = []

  for (let count = 0; count < numYears; count++) {
    let eventNum = count + 1
    const newValue = currentValue / Math.pow(rate, eventNum)
    const diffValue = value - newValue
    value = newValue

    schedule.push({
      eventNum,
      diffValue,
      value,
    })
  }

  return {
    currentValue,
    newValue: value,
    numYears,
    schedule,
  }
}

const getFrequencyPeriods = (frequency) => {
  let period = 1

  if (frequency === 'annually') {
  }

  if (frequency === 'semi-annually') {
    period = Math.floor(period * 2)
  }

  if (frequency === 'quarterly') {
    period = Math.floor(period * 4)
  }

  if (frequency === 'monthly') {
    period = Math.floor(period * 12)
  }

  if (frequency === 'weekly') {
    period = Math.floor(period * 52)
  }

  if (frequency === 'daily') {
    period = Math.floor(period * 365)
  }

  return {
    period,
  }
}

export const getNumMonths = (futureValue, monthlyPayment, interestRate, presentValue = 0) => {
  const rate = interestRate / 100 / 12
  const targetValue = currency(futureValue)
  const depositValue = currency(monthlyPayment)
  let numMonths = 0
  let amount = currency(presentValue)

  for (let count = 1; count <= 10000; count++) {
    let interest = currency(amount).multiply(rate)
    amount = amount.add(interest)
    amount = amount.add(depositValue)

    if (amount.value >= targetValue.value) {
      numMonths = count
      break
    }
  }

  return numMonths
}

export const getCompoundInterestRate = (futureValue, presentValue, frequency, numYears) => {
  const freq = getFrequencyPeriods(frequency)

  const rate = freq.period * (Math.pow(currency(futureValue).divide(presentValue), 1 / (freq.period * numYears)) - 1)

  return rate * 100
}

export const getNeededDeposit = (presentValue, futureValue, numYears, frequency, interestRate) => {
  const freq = getFrequencyPeriods(frequency)
  const rate = interestRate / 100 / freq.period
  const r1 = Math.pow(1 + rate, numYears * freq.period)

  const deposit = currency(rate * currency(futureValue).subtract(currency(presentValue).multiply(r1))).divide(r1 - 1)

  return deposit
}

export const getInvestment = (opts) => {
  const dayDiff = differenceInDays(normalizedDate(opts.futureDate), normalizedDate(opts.presentDate))
  const numYears = opts.numYears ?? dayDiff / 365
  const today = normalizedDate(new Date())

  let rate = opts.interestRate / 100
  let period = numYears
  let addPeriodFunc = addMonths
  let addPeriodDelta = 1

  if (opts.frequency === 'annually') {
    addPeriodFunc = addYears
  }

  if (opts.frequency === 'semi-annually') {
    rate = rate / 2
    period = Math.floor(period * 2)
    addPeriodDelta = 6
  }

  if (opts.frequency === 'quarterly') {
    rate = rate / 4
    period = Math.floor(period * 4)
    addPeriodDelta = 3
  }

  if (opts.frequency === 'monthly') {
    rate = rate / 12
    period = Math.floor(period * 12)
  }

  if (opts.frequency === 'weekly') {
    rate = rate / 52
    period = Math.floor(period * 52)
    addPeriodFunc = addWeeks
  }

  if (opts.frequency === 'daily') {
    rate = rate / 365
    period = Math.floor(period * 365)
    addPeriodFunc = addDays
  }

  const schedule = []
  let futureValue = currency(opts.presentValue)
  let totalDeposit = currency(0)
  let totalInterest = currency(0)
  let totalNetChange = currency(0)

  for (let count = 0; count < period; count++) {
    const eventNum = count + 1
    const date = addPeriodFunc(today, eventNum * addPeriodDelta)

    let interest = currency(futureValue).multiply(rate)
    futureValue = futureValue.add(interest)
    totalInterest = totalInterest.add(interest)

    let deposit = currency(opts.depositValue ?? 0)
    futureValue = futureValue.add(deposit)
    totalDeposit = totalDeposit.add(deposit)

    let netChange = opts.keepRunningTotals ? deposit.add(interest) : interest
    totalNetChange = totalNetChange.add(netChange)

    schedule.push({
      eventNum,
      date,
      formattedDate: format(date, 'MM-dd-yyyy'),
      deposit,
      interest,
      totalDeposit,
      totalInterest,
      netChange,
      balance: opts.keepRunningTotals ? futureValue : currency(opts.presentValue).add(totalNetChange),
    })
  }

  return {
    presentDate: opts.presentDate,
    futureDate: opts.futureDate,
    presentValue: opts.presentValue,
    depositValue: opts.depositValue,
    futureValue,
    interestRate: opts.interestRate,
    frequency: opts.frequency,
    schedule,
    totalDeposit,
    totalInterest,
    totalNetChange,
    totalBalance: futureValue,
  }
}

export const getInvestmentByYear = (investment, groupByCalendarYear = true) => {
  const groupedSchedule = []
  let scheduleYear = null
  let currYearGroup = null

  const freq = getFrequencyPeriods(investment.frequency)

  for (const [index, event] of investment.schedule.entries()) {
    const date = event.date

    const shouldStartNewYear = groupByCalendarYear
      ? date.getFullYear() !== scheduleYear
      : index === 0 || index % freq.period === 0

    if (shouldStartNewYear) {
      if (currYearGroup) {
        groupedSchedule.push(currYearGroup)
      }

      currYearGroup = {
        events: [],
        label: date.getFullYear(),
        totals: {
          deposit: 0,
          interest: 0,
          netChange: 0,
          balance: 0,
        },
      }

      scheduleYear = date.getFullYear()
    }

    currYearGroup.events.push({ ...event })

    currYearGroup.totals.deposit = currency(currYearGroup.totals.deposit).add(event.deposit)
    currYearGroup.totals.interest = currency(currYearGroup.totals.interest).add(event.interest)
    currYearGroup.totals.netChange = currency(currYearGroup.totals.netChange).add(event.netChange)
    currYearGroup.totals.balance = event.balance

    if (index === investment.schedule.length - 1) {
      groupedSchedule.push(currYearGroup)
    }
  }

  return groupedSchedule
}

export const getAmortization = (principal, interestRate, numPayments, payment = null) => {
  let balance = principal
  let totalPayment = 0
  let totalInterest = 0
  const monthlyRate = interestRate / 100 / 12
  const monthlyPayment = payment
    ? currency(payment)
    : currency(balance).multiply(monthlyRate / (1 - Math.pow(1 + monthlyRate, -numPayments)))
  const paymentSchedule = []
  const today = normalizedDate(new Date())

  for (let count = 0; count < numPayments; count++) {
    let payment = monthlyPayment
    let interest = 0
    let principal = 0
    const eventNum = count + 1
    const isLastPayment = balance.intValue <= payment.intValue || count === numPayments - 1
    const date = addMonths(today, eventNum)

    interest = currency(balance).multiply(monthlyRate)

    if (isLastPayment) {
      payment = currency(balance).add(interest)
    }

    principal = currency(payment).subtract(interest)
    balance = currency(balance).subtract(principal)
    totalInterest = currency(totalInterest).add(interest)
    totalPayment = currency(totalPayment).add(payment)

    paymentSchedule.push({
      eventNum,
      date,
      formattedDate: format(date, 'MM-dd-yyyy'),
      payment,
      interest,
      principal,
      balance,
    })
  }

  return {
    numPayments,
    monthlyPayment,
    totalPayment,
    totalInterest,
    totalPrincipal: principal,
    interestRate: interestRate,
    paymentSchedule,
  }
}

export const getFixedMortgageAmortization = (opts) => {
  let balance = opts.principal
  let totalPayment = 0
  let totalInterest = 0
  const monthlyRate = opts.interestRate / 100 / 12
  const monthlyPayment = opts.payment
    ? currency(opts.payment)
    : currency(balance).multiply(monthlyRate / (1 - Math.pow(1 + monthlyRate, -opts.numPayments)))
  const paymentSchedule = []
  const startDate = opts.startDate ?? new Date()
  const start = subMonths(normalizedDate(startDate), 1)

  if (opts.startingEvent) {
    paymentSchedule.push(opts.startingEvent)
  }

  for (let count = 0; count < opts.numPayments; count++) {
    let payment = monthlyPayment
    let interest = 0
    let principal = 0
    const eventNum = count + 1
    const isLastPayment = balance.intValue <= payment.intValue || count === opts.numPayments - 1
    const date = addMonths(start, eventNum)

    interest = currency(balance).multiply(monthlyRate)

    if (isLastPayment) {
      payment = currency(balance).add(interest)
    }

    principal = currency(payment).subtract(interest)
    balance = currency(balance).subtract(principal)
    totalInterest = currency(totalInterest).add(interest)
    totalPayment = currency(totalPayment).add(payment)

    paymentSchedule.push({
      eventNum,
      date,
      formattedDate: format(date, 'MM-dd-yyyy'),
      payment,
      interest,
      principal,
      balance,
    })
  }

  return {
    numPayments: opts.numPayments,
    monthlyPayment,
    totalPayment,
    totalInterest,
    totalPrincipal: opts.principal,
    paymentSchedule,
  }
}

const shouldAdjust = (period, currentRate, opts) => {
  const rateCap = opts.rateCap / 100 / 12

  if (currentRate >= rateCap) {
    return false
  }

  if (opts.numMonthsBefore === period) {
    return true
  }

  if (period > opts.numMonthsBefore && period % opts.numMonthsBetween === 0) {
    return true
  }

  return false
}

const adjustRate = (period, currentRate, opts) => {
  const rateCap = opts.rateCap / 100 / 12
  let newRate = currentRate

  if (opts.numMonthsBefore === period) {
    newRate = newRate + opts.maxFirstRate / 100 / 12
  }

  if (period > opts.numMonthsBefore && period % opts.numMonthsBetween === 0) {
    newRate = newRate + opts.maxRate / 100 / 12
  }

  if (newRate >= rateCap) {
    newRate = rateCap
  }

  return newRate
}

const adjustMonthlyPayment = (balance, rate, numPayments) => {
  return currency(balance).multiply(rate / (1 - Math.pow(1 + rate, -numPayments)))
}

export const getAdjustableMortgageAmortization = (opts) => {
  let balance = opts.principal
  let totalPayment = 0
  let totalInterest = 0
  let monthlyRate = opts.interestRate / 100 / 12
  const monthlyPayment = opts.payment
    ? currency(opts.payment)
    : currency(balance).multiply(monthlyRate / (1 - Math.pow(1 + monthlyRate, -opts.numPayments)))
  const paymentSchedule = []
  const startDate = opts.startDate ?? new Date()
  const start = subMonths(normalizedDate(startDate), 1)

  if (opts.startingEvent) {
    paymentSchedule.push(opts.startingEvent)
  }

  let currentPeriodRate = monthlyRate
  let currentPeriodPayment = monthlyPayment

  for (let count = 0; count < opts.numPayments; count++) {
    if (shouldAdjust(count, currentPeriodRate, opts)) {
      currentPeriodRate = adjustRate(count, currentPeriodRate, opts)
      currentPeriodPayment = adjustMonthlyPayment(balance, currentPeriodRate, opts.numPayments - count)
    }

    let payment = currentPeriodPayment
    let interest = 0
    let principal = 0
    const eventNum = count + 1
    const isLastPayment = balance.intValue <= payment.intValue || count === opts.numPayments - 1
    const date = addMonths(start, eventNum)

    interest = currency(balance).multiply(currentPeriodRate)

    if (isLastPayment) {
      payment = currency(balance).add(interest)
    }

    principal = currency(payment).subtract(interest)
    balance = currency(balance).subtract(principal)
    totalInterest = currency(totalInterest).add(interest)
    totalPayment = currency(totalPayment).add(payment)

    paymentSchedule.push({
      eventNum,
      date,
      formattedDate: format(date, 'MM-dd-yyyy'),
      payment,
      interest,
      principal,
      balance,
    })
  }

  return {
    numPayments: opts.numPayments,
    monthlyPayment,
    totalPayment,
    totalInterest,
    totalPrincipal: opts.principal,
    paymentSchedule,
  }
}

export const getAmortizationByYear = (amortization) => {
  const groupedSchedule = []
  let paymentScheduleYear = null
  let currYearGroup = null

  for (const [index, event] of amortization.paymentSchedule.entries()) {
    const date = event.date

    if (date.getFullYear() !== paymentScheduleYear) {
      if (currYearGroup) {
        groupedSchedule.push(currYearGroup)
      }

      currYearGroup = {
        events: [],
        label: date.getFullYear(),
        totals: {
          payment: 0,
          interest: 0,
          principal: 0,
        },
      }

      paymentScheduleYear = date.getFullYear()
    }

    currYearGroup.events.push({ ...event })

    currYearGroup.totals.payment = currency(currYearGroup.totals.payment).add(event.payment)
    currYearGroup.totals.interest = currency(currYearGroup.totals.interest).add(event.interest)
    currYearGroup.totals.principal = currency(currYearGroup.totals.principal).add(event.principal)

    if (index === amortization.numPayments - 1) {
      groupedSchedule.push(currYearGroup)
    }
  }

  return groupedSchedule
}
